import { fetchNotifications, markAllNotificationsRead, markNotificationsRead } from '@api/useQueryCalls';
import { generalEmptyState } from '@assets/png/imagesPng';
import LocalImage from '@atoms/LocalImage';
import NotificationFilters, { DEFAULT_NOTIFICATION_FILTER } from '@atoms/Notifications/NotificationFilters';
import NotificationList, {
  getNotificationApiCategory,
  getNotificationSections,
} from '@atoms/Notifications/NotificationList';
import { normalizeNotificationApiItem, NotificationApiItem } from '@atoms/Notifications/NotificationList/notificationData';
import {
  logInboxItemViewed,
  logInboxListViewed,
  logInboxMarkAllAsReadClicked,
  logInboxScrolled,
} from '@atoms/Notifications/notificationAnalytics';
import NotificationPermissionBanner from '@atoms/Notifications/NotificationPermissionBanner';
import { Text, View } from '@components/primitives';
import { useToast } from '@hooks';
import appStyles from '@root/appStyles';
import { useQueryClient } from '@tanstack/react-query';
import colors from '@theme/colors';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, StatusBar } from 'react-native';
import { useTranslation } from 'react-i18next';
import { showSuccessToast } from '@utils/toastManager';
import styles from './styles';

export const hasUnreadNotifications = (notifications: NotificationApiItem[]): boolean =>
  notifications.some(notification => !notification.isRead);

export const markAllNotificationsAsRead = (notifications: NotificationApiItem[]): NotificationApiItem[] =>
  notifications.map(notification => ({
    ...notification,
    isRead: true,
    readAt: notification.readAt ?? new Date().toISOString(),
  }));

export const updateNotificationsInfiniteQueryData = (queryData: any, notifications: NotificationApiItem[]) => {
  if (!queryData?.pages) return queryData;

  let notificationIndex = 0;

  return {
    ...queryData,
    pages: queryData.pages.map(page => {
      const pageNotifications = page?.notifications ?? [];
      const nextNotifications = notifications.slice(notificationIndex, notificationIndex + pageNotifications.length);
      notificationIndex += pageNotifications.length;

      return {
        ...page,
        unreadCount: 0,
        notifications: nextNotifications,
      };
    }),
  };
};

function GGNotificationsInbox(): React.JSX.Element {
  const { i18n, t } = useTranslation('common');
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selectedFilter, setSelectedFilter] = useState(DEFAULT_NOTIFICATION_FILTER);
  const [notifications, setNotifications] = useState<NotificationApiItem[]>([]);
  const hasLoggedInboxViewedRef = useRef(false);
  const hasLoggedScrollRef = useRef(false);
  const viewedNotificationItemsRef = useRef<Record<string, any>>({});
  const pendingReadNotificationIdsRef = useRef<Record<string, boolean>>({});
  const notificationQueryPayload = {
    limit: 10,
    category: getNotificationApiCategory(selectedFilter),
  };
  const {
    data: notificationsResponse,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = fetchNotifications(notificationQueryPayload);
  const { mutate: submitMarkAllRead, isLoading: isMarkingAllRead } = markAllNotificationsRead({
    onSuccess: () => {
      setNotifications(previousNotifications =>
        previousNotifications.map(notification => ({
          ...notification,
          isRead: true,
          readAt: notification.readAt ?? new Date().toISOString(),
        })),
      );
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
  const { mutate: submitMarkNotificationsRead } = markNotificationsRead({
    onSuccess: (_response, payload) => {
      const notificationIds = payload.notificationIds || [];

      setNotifications(previousNotifications =>
        previousNotifications.map(notification =>
          notificationIds.includes(notification.id)
            ? {
                ...notification,
                isRead: true,
                readAt: notification.readAt ?? new Date().toISOString(),
              }
            : notification,
        ),
      );
      notificationIds.forEach(notificationId => {
        delete pendingReadNotificationIdsRef.current[notificationId];
      });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (_error, payload) => {
      (payload.notificationIds || []).forEach(notificationId => {
        delete pendingReadNotificationIdsRef.current[notificationId];
      });
    },
  });

  useEffect(() => {
    const apiNotifications = notificationsResponse?.pages?.flatMap(page => (page?.notifications ?? []).map(normalizeNotificationApiItem)) ?? [];

    setNotifications(apiNotifications);
  }, [notificationsResponse, selectedFilter]);
  const responseNotifications =
    notificationsResponse?.pages?.flatMap(page => (page?.notifications ?? []).map(normalizeNotificationApiItem)) ?? [];

  const visibleNotifications = useMemo(
    () =>
      notifications.filter(notification => {
        if (selectedFilter === 'all') return true;
        if (selectedFilter === 'info')
          return notification.category === 'information' || notification.category === 'informasi';

        return notification.category === 'message';
      }),
    [notifications, selectedFilter],
  );
  const isMarkAllReadDisabled = isMarkingAllRead || !hasUnreadNotifications(visibleNotifications);

  useEffect(() => {
    if (!hasLoggedInboxViewedRef.current) {
      hasLoggedInboxViewedRef.current = true;
      logInboxListViewed(visibleNotifications);
    }
  }, [visibleNotifications]);

  const notificationSections = getNotificationSections({
    notifications,
    selectedFilter,
    language: i18n.language,
    t,
  });
  const isInitialLoading = !notificationsResponse;
  const isSyncingNotifications = Boolean(notificationsResponse) && responseNotifications.length > 0 && notifications.length === 0;
  const hasNotificationData = notificationSections.length > 0;

  return (
    <View style={[appStyles.flex1, styles.container]}>
      <StatusBar backgroundColor={colors.secondary.orchid} translucent={false} hidden={false} />
      <View style={styles.filtersWrapper}>
        <NotificationFilters onSelectFilter={setSelectedFilter} selectedFilter={selectedFilter} />
      </View>
      <View style={styles.bannerWrapper}>
        <NotificationPermissionBanner />
      </View>
      <View style={styles.content}>
        {isInitialLoading || isSyncingNotifications ? (
          <View style={styles.loaderContainer} testID="notifications-initial-loader">
            <ActivityIndicator color={colors.secondary.orchid} />
          </View>
        ) : hasNotificationData ? (
          <View style={styles.listWrapper}>
            <NotificationList
              isMarkAllReadDisabled={isMarkAllReadDisabled}
              isPaginating={isFetchingNextPage}
              onMarkAllRead={() => {
                const nextNotifications = markAllNotificationsAsRead(notifications);

                setNotifications(nextNotifications);
                queryClient.setQueriesData({ queryKey: ['notifications'] }, queryData =>
                  updateNotificationsInfiniteQueryData(queryData, nextNotifications),
                );
                logInboxMarkAllAsReadClicked(visibleNotifications);
                showSuccessToast(toast, t('notifications.list.markAllReadSuccess'), 'notificationsMarkAllRead');
                submitMarkAllRead();
              }}
              onReachEnd={() => {
                if (hasNextPage) fetchNextPage();
              }}
              onScrollStart={() => {
                if (!hasLoggedScrollRef.current) {
                  hasLoggedScrollRef.current = true;
                  logInboxScrolled();
                }
              }}
              onVisibleItemsChanged={items => {
                let hasNewVisibleItem = false;
                const unreadVisibleIdsToMark = items
                  .filter(item => !item.isRead && !pendingReadNotificationIdsRef.current[item.id])
                  .map(item => item.id);

                items.forEach(item => {
                  if (!viewedNotificationItemsRef.current[item.id]) {
                    viewedNotificationItemsRef.current[item.id] = item;
                    hasNewVisibleItem = true;
                  }
                });

                if (hasNewVisibleItem) {
                  logInboxItemViewed(Object.values(viewedNotificationItemsRef.current));
                }

                if (unreadVisibleIdsToMark.length > 0) {
                  unreadVisibleIdsToMark.forEach(notificationId => {
                    pendingReadNotificationIdsRef.current[notificationId] = true;
                  });
                  submitMarkNotificationsRead({ notificationIds: unreadVisibleIdsToMark });
                }
              }}
              sections={notificationSections}
            />
          </View>
        ) : (
          <View style={styles.emptyStateContainer}>
            <LocalImage
              source={generalEmptyState}
              style={styles.emptyStateImage}
              contentFit="contain"
              testID="notifications-empty-art"
            />
            <Text color={colors.neutral.charcoal} textAlign="center" variant="lg-bold">
              {t('notifications.emptyTitle')}
            </Text>
            <Text
              color={colors.neutral.charcoal}
              style={styles.emptyStateDescription}
              textAlign="center"
              variant="md-normal">
              {t('notifications.emptyDescription')}
            </Text>
          </View>
        )}
      </View>
    </View>
  );
}

export default GGNotificationsInbox;
